import React, { useState, useEffect } from "react"

const TAG_COLORS = [
  "bg-blue-100 text-blue-700",
  "bg-green-100 text-green-700",
  "bg-purple-100 text-purple-700",
  "bg-orange-100 text-orange-700",
  "bg-pink-100 text-pink-700",
  "bg-teal-100 text-teal-700",
  "bg-yellow-100 text-yellow-800",
  "bg-red-100 text-red-700",
]

const getTagColor = (tag) => {
  let hash = 0
  for (let i = 0; i < tag.length; i++) hash = (hash * 31 + tag.charCodeAt(i)) | 0
  return TAG_COLORS[Math.abs(hash) % TAG_COLORS.length]
}
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import Input from "aldoo-ra/Input"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import ToggleSwitch from "aldoo-ra/ToggleSwitch"
import { XMarkIcon } from "@heroicons/react/24/outline"
import { UpdateTicket, GetTags } from "./api"
import { showNotification } from "aldoo-ra/Notification"

const TagDialog = ({
  isOpen,
  ticketId,
  ticketLabel,
  currentTags = [],
  onClose,
  onUpdated,
  onTagApplied,
  ticket,
  // Bulk operation props
  ticketIds = [],
  ticketLabels = [],
}) => {
  const [tags, setTags] = useState([])
  const [newTag, setNewTag] = useState("")
  const [loading, setLoading] = useState(false)
  const [availableTags, setAvailableTags] = useState([])
  const [appendMode, setAppendMode] = useState(true) // true = append, false = replace

  // Fetch all available tags from the system
  useEffect(() => {
    async function loadTags() {
      try {
        const response = await GetTags()
        if (response.result) {
          const tagOptions = response.data.map((tag) => ({
            value: tag,
            label: tag,
          }))
          setAvailableTags(tagOptions)
        }
      } catch (error) {
        showNotification({
          message: "Failed to load tags",
          className: "bg-admin_error text-white",
        })
      }
    }

    if (isOpen) {
      loadTags()
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen) {
      setTags(currentTags)
      setNewTag("")
      setAppendMode(true) // Reset to append mode by default
    }
  }, [isOpen, currentTags])

  // Convert current tags array to the format expected by DropDownSelect
  const selectedTagsForDropdown = tags.map(tag => ({
    value: tag,
    label: tag
  }))

  const handleAddTag = () => {
    if (!newTag.trim()) return

    const trimmedTag = newTag.trim()
    if (!tags.includes(trimmedTag)) {
      setTags([...tags, trimmedTag])
      setNewTag("")
    }
  }

  // Handler for dropdown selection changes
  const handleTagSelect = (selectedOptions) => {
    // Extract just the tag values from the selected options
    const selectedTags = selectedOptions.map(option => option.value)
    setTags(selectedTags)
  }

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter((tag) => tag !== tagToRemove))
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault()
      handleAddTag()
    }
  }

  const handleApply = async () => {
    try {
      setLoading(true)

      // Check if a new tag needs to be included
      let updatedTags = [...tags]
      const trimmedTag = newTag.trim()

      if (trimmedTag && !updatedTags.includes(trimmedTag)) {
        updatedTags.push(trimmedTag)
      }

      // Check if this is for a new ticket (no ticketId or ticket has no ID)
      const isNewTicket = !ticketId || (ticket && !ticket.ticketID)

      if (isNewTicket && onTagApplied) {
        // If it's a new ticket and we have onTagApplied, use it
        onTagApplied(updatedTags)
        onClose()
      } else {
        // Check if this is a bulk operation
        const isBulkOperation = ticketIds.length > 0
        
        if (isBulkOperation) {
          // Bulk update for multiple tickets
          const promises = ticketIds.map(id => 
            UpdateTicket({
              ticketId: id,
              tags: updatedTags,
              tagOperation: appendMode ? 'append' : 'replace', // Add operation mode
            })
          )
          
          await Promise.all(promises)

          showNotification({
            message: `Tags updated successfully for ${ticketIds.length} ticket${ticketIds.length > 1 ? 's' : ''}`,
            className: "bg-admin_primary text-admin_text_inv",
          })
        } else {
          // Single ticket update
          await UpdateTicket({
            ticketId,
            tags: updatedTags,
          })

          showNotification({
            message: "Tags updated successfully",
            className: "bg-admin_primary text-admin_text_inv",
          })
        }

        onUpdated && onUpdated()
        onClose()
      }
    } catch (error) {
      showNotification({
        message: "Failed to update tags",
        className: "bg-admin_error text-white",
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      variant="admin_centered"
      className="w-full md:w-[600px]"
    >
      <div className="p-4">
        {/* Header */}
        <div className="mb-6">
          <Typography variant="admin_h4">
            Ticket Tag Editor
          </Typography>
        </div>

        {/* Ticket Info */}
        <div className="bg-admin_info_bg rounded-md p-4 mb-6">
          {ticketIds.length > 0 ? (
            // Bulk operation display
            <>
              <Typography variant="admin_body" className="font-medium mb-2">
                Selected Tickets ({ticketIds.length}):
              </Typography>
              <div className="space-y-1">
                {ticketLabels.slice(0, 5).map((label, index) => (
                  <div key={index} className="text-[14px] leading-tight font-normal text-admin_text_secondary">
                    {label}
                  </div>
                ))}
                {ticketLabels.length > 5 && (
                  <div className="text-[14px] leading-tight font-normal text-admin_text_secondary italic">
                    and (+{ticketLabels.length - 5} more)
                  </div>
                )}
              </div>
            </>
          ) : (
            // Single ticket display
            <Typography variant="admin_body" className="text-center">
              {ticketLabel}
            </Typography>
          )}
        </div>

        {/* Append/Replace Toggle - Only show for bulk operations */}
        {ticketIds.length > 0 && (
          <div className="mb-6 p-4 border border-admin_bordered rounded-md bg-admin_paper/50">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <Typography variant="admin_body" className="font-medium mb-1">
                  Tag Operation Mode
                </Typography>
                <Typography variant="admin_txt1" className="text-admin_text_secondary">
                  {appendMode 
                    ? "Append to existing tags - new tags will be added to each ticket's current tags"
                    : "Replace existing tags - all current tags will be removed and replaced with new ones"
                  }
                </Typography>
              </div>
              <div className="ml-4 flex items-center space-x-3">
                <Typography 
                  variant="admin_txt1" 
                  className={`${appendMode ? 'text-admin_text font-medium' : 'text-admin_text_secondary'}`}
                >
                  Append
                </Typography>
                <ToggleSwitch
                  variant="admin"
                  isOn={!appendMode}
                  onChange={(isReplace) => setAppendMode(!isReplace)}
                />
                <Typography 
                  variant="admin_txt1" 
                  className={`${!appendMode ? 'text-admin_text font-medium' : 'text-admin_text_secondary'}`}
                >
                  Replace
                </Typography>
              </div>
            </div>
          </div>
        )}

        {/* Add Tag Input and Dropdown */}
        <div className="mb-6 flex items-center gap-4">
          <div className="flex-grow relative">
            <Input
              variant="admin"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Add Custom Tag"
              className="pr-[65px]"
            />
            {newTag.trim() && (
              <Button
                variant="admin_primary"
                onClick={handleAddTag}
                className="w-[60px] absolute right-[66px] top-1/2 transform -translate-y-1/2 text-xs"
              >
                Add
              </Button>
            )}
          </div>

          <div className="w-48">
            <DropDownSelect
              variant="admin"
              options={availableTags}
              value={selectedTagsForDropdown}
              onChange={handleTagSelect}
              searchEnabled
              multiSelection={true}
              placeholder="Select Tag"
              className="w-full"
            />
          </div>
        </div>

        {/* Tags Display Area */}
        <div className="min-h-[200px] border border-admin_bordered rounded-md p-4 mb-6">
          <div className="flex flex-wrap gap-2">
            {tags.map((tag, index) => (
              <div
                key={index}
                className={`inline-flex items-center rounded-full px-3 py-1 ${getTagColor(tag)}`}
              >
                <Typography variant="admin_txt1">{tag}</Typography>

                <button
                  onClick={() => handleRemoveTag(tag)}
                  className="ml-2 hover:text-admin_text_secondary"
                >
                  <XMarkIcon className="w-4 h-4 stroke-2" />
                </button>
              </div>
            ))}
            {tags.length === 0 && (
              <Typography variant="admin_body_dim" className="my-0">
                {ticketIds.length > 0 
                  ? "No tags added - tags will be applied to all selected tickets" 
                  : "No tags added"
                }
              </Typography>
            )}
          </div>
        </div>

        {/* Apply Button */}
        <Button
          variant="admin_primary"
          onClick={handleApply}
          disabled={loading}
          className="w-full"
        >
          {loading 
            ? "Applying..." 
            : ticketIds.length > 0 
              ? "Apply to All Selected Tickets" 
              : "Apply"
          }
        </Button>
      </div>
    </Modal>
  )
}

export default TagDialog